import React, { useEffect, useState } from 'react';

import { WindowLocation } from '@reach/router';
import { LayoutBodyStyles, StyledMain } from './Layout.styles';

import { propagateUtmParams } from 'src/functions/propagateQueryParams';

import ProfitSalesEmailPopupMain, {
  ProfitSalesEmailPopupMainProps,
} from 'src/organisms/Shared/2023_Q4/EmailPopup/ProfitSalesEmailPopup/ProfitSalesEmailPopup';

import '../Layout.fonts.scss';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import Header from 'src/molecules/Shared/2023_Q4/Header/Header';
import Footer from 'src/organisms/Shared/2023_Q4/Footer/Footer';
import { HeaderDesignControllerContextData } from 'src/hooks/useHeaderDesignController';
import { ColorScheme } from 'src/styles/functions/getButtonStyleByColorScheme';
import ProEmailPopup from 'src/organisms/Pro/Shared/ProEmailPopup/ProEmailPopup';
import { ExpandedMenuControllerProvider } from 'src/hooks/useExpandedMenuController';

export type LayoutTheme = 'standard' | 'pro';

export interface LayoutProps<LocationState = WindowLocation['state']> {
  children: React.ReactNode;
  location?: WindowLocation<LocationState>;
  footer?: boolean | (() => JSX.Element);
  topbar?: boolean | (() => JSX.Element);
  topbarTheme?: {
    defaultBackgroundTheme?: HeaderDesignControllerContextData['backgroundTheme'];
    defaultTextColorTheme?: HeaderDesignControllerContextData['textColorTheme'];
    unpinnedBackgroundTheme?: HeaderDesignControllerContextData['backgroundTheme'];
    unpinnedTextColorTheme?: HeaderDesignControllerContextData['textColorTheme'];
  };
  footerCallToActionTheme?: ColorScheme;
  hideEmailPopup?: boolean;
  theme?: LayoutTheme;
  bodyWidth?: string;
  bodyBackgroundColor?: string;
  popupModalCustomization?: ProfitSalesEmailPopupMainProps['customization'];
  headerCallToActionStatsigEvent?: string;
}

// TODO: Move Footer, Menu and Helm here https://mightynetworks.atlassian.net/browse/MNW-274
const Layout = ({
  children,
  location,
  footer = true,
  topbar = true,
  topbarTheme = {},
  hideEmailPopup = false,
  theme = 'standard',
  bodyWidth = '100vw',
  bodyBackgroundColor = null,
  footerCallToActionTheme,
  popupModalCustomization,
  headerCallToActionStatsigEvent,
}: LayoutProps): JSX.Element => {
  const [chromeHidden, setChromeHidden] = useState(false);

  const windowAvailable = typeof window !== 'undefined';

  useEffect(() => {
    propagateUtmParams();
  }, []);

  useEffect(() => {
    if (windowAvailable) {
      const inAppView = window?.location?.search.includes('hide_chrome=1');
      if (inAppView) {
        window.sessionStorage.setItem('hide_chrome', '1');
        setChromeHidden(true);
      } else {
        const inAppViewCache = window.sessionStorage.getItem('hide_chrome') === '1';
        setChromeHidden(inAppViewCache);
      }
    }
  }, [windowAvailable]);

  const renderFooter = () => {
    // Footer controls
    if (chromeHidden || !footer) return null;

    if (typeof footer === 'function') {
      return footer();
    }

    return (
      <Footer
        formCallToActionColorScheme={footerCallToActionTheme}
        theme={theme}
      />
    );
  };

  const renderHeader = () => {
    // Header controls
    if (chromeHidden || !topbar) return null;

    if (typeof topbar === 'function') {
      return topbar();
    }

    return (
      <ExpandedMenuControllerProvider>
        <Header
          locationProps={location}
          linksCollection={theme}
          logo={theme}
          callToAction={theme}
          callToActionStatsigEvent={headerCallToActionStatsigEvent}
          {...topbarTheme}
        />
      </ExpandedMenuControllerProvider>
    );
  };

  const renderEmailPopup = () => {
    if (hideEmailPopup) return null;

    if (theme === 'pro') {
      return <ProEmailPopup />;
    }

    return <ProfitSalesEmailPopupMain customization={popupModalCustomization} />;
  };

  return (
    <StyledMain id='styled-main'>
      <LayoutBodyStyles
        width={bodyWidth}
        backgroundColor={bodyBackgroundColor}
      />
      {renderEmailPopup()}
      {renderHeader()}

      {children}

      {renderFooter()}
    </StyledMain>
  );
};

export default Layout;
